import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, fetchReviews } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ReviewCard } from "../components/ReviewCard";
import { ReviewForm } from "../components/ReviewForm";
import { StarRating } from "../components/StarRating";
import { Seo } from "../components/Seo";

function productJsonLd(product, reviews, rating) {
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.short_description,
    image: product.image,
    sku: product.id,
    brand: { "@type": "Brand", name: "Peptide Labs N.I." },
    offers: {
      "@type": "Offer",
      price: product.price.toFixed(2),
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      url: window.location.href,
    },
  };
  if (rating.count) {
    ld.aggregateRating = { "@type": "AggregateRating", ratingValue: rating.avg.toFixed(1), reviewCount: rating.count };
    ld.review = reviews.slice(0, 5).map((r) => ({
      "@type": "Review",
      name: r.title,
      reviewBody: r.body,
      datePublished: r.created_at?.slice(0, 10),
      author: { "@type": "Person", name: r.author_name },
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 },
    }));
  }
  return ld;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    fetchProduct(slug).then(setProduct).catch(() => {});
    fetchReviews(slug).then(setReviews).catch(() => {});
  }, [slug]);

  const ratingSummary = useMemo(() => {
    if (!reviews.length) return { avg: 0, count: 0 };
    const sum = reviews.reduce((a, r) => a + r.rating, 0);
    return { avg: sum / reviews.length, count: reviews.length };
  }, [reviews]);

  if (!product) {
    return (
      <div className="max-w-[1360px] mx-auto px-6 lg:px-10 py-24 text-[#6E7784]">Loading…</div>
    );
  }

  const onAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to cart`);
  };

  const onReviewSubmitted = (created) => {
    setReviews((prev) => [created, ...prev]);
  };

  return (
    <div data-testid="product-detail-page" className="max-w-[1360px] mx-auto px-6 lg:px-10 py-12">
      <Seo
        title={`Buy ${product.name} ${product.strength} UK`}
        description={`${product.name} ${product.strength} research peptide, ${product.purity} purity, £${product.price.toFixed(2)}. ${product.short_description} UK dispatch. For research use only.`}
        image={product.image}
        type="product"
        jsonLd={productJsonLd(product, reviews, ratingSummary)}
      />
      <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#6E7784] hover:text-white transition-colors">
        <ArrowLeft size={14} /> Back to catalog
      </Link>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-6 relative">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden vial-shell">
            <div className="absolute inset-0 grid-backdrop opacity-30" />
            {product.is_bundle ? (
              <div className={`absolute inset-0 flex items-center justify-center px-8 ${(product.includes || []).length >= 3 ? "gap-3" : "gap-5"}`}>
                {(product.includes || []).map((line, i, arr) => {
                  const total = arr.length;
                  const isCenter = total === 3 && i === 1;
                  const rot = total === 3 ? (i === 0 ? -6 : i === 2 ? 6 : 0) : (i === 0 ? -4 : 4);
                  const ty = total === 3 ? (isCenter ? 0 : i === 0 ? 14 : -14) : (i === 0 ? 14 : -14);
                  const w = total === 3 ? "w-[26%]" : "w-[36%]";
                  const parts = line.trim().split(/\s+/);
                  const strength = parts.slice(-2).join(" ");
                  const name = parts.slice(0, -2).join(" ") || line;
                  return (
                    <div
                      key={i}
                      className={`relative ${w} h-[78%] rounded-t-[18px] rounded-b-[10px] border border-[#2A2A2A] bg-[#0e0e0e] overflow-hidden`}
                      style={{ transform: `translateY(${ty}px) rotate(${rot}deg)` }}
                    >
                      <div className="absolute top-0 left-0 right-0 h-[10%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
                      <div className="absolute top-[10%] left-0 right-0 h-[3%] bg-[#171717] border-b border-[#2A2A2A]" />
                      <div className="absolute left-0 right-0 bottom-0 h-[60%] vial-liquid" />
                      <div className="absolute left-1/2 -translate-x-1/2 top-[32%] w-[86%] px-2 py-2 bg-[#0B0B0B]/85 border border-[#2A2A2A] rounded-[6px]">
                        <div className="label-mono text-[8px]">Peptide Labs N.I.</div>
                        <div className="mt-1.5 text-white text-[10.5px] font-medium tracking-tight leading-tight line-clamp-1">{name}</div>
                        <div className="mt-0.5 text-[#B7BCC7] text-[9px]">{strength}</div>
                      </div>
                      <div className="absolute left-0 right-0 bottom-[58%] h-[1.5px] bg-[#1565FF]/70 blur-[2px]" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[46%] h-[80%] rounded-t-[20px] rounded-b-[10px] border border-[#2A2A2A] bg-[#0e0e0e] overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[10%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
                <div className="absolute top-[10%] left-0 right-0 h-[3%] bg-[#171717] border-b border-[#2A2A2A]" />
                <div className="absolute left-0 right-0 bottom-0 h-[60%] vial-liquid" />
                <div className="absolute left-1/2 -translate-x-1/2 top-[34%] w-[86%] px-3 py-3 bg-[#0B0B0B]/85 border border-[#2A2A2A] rounded-[8px]">
                  <div className="label-mono text-[9px]">Peptide Labs N.I.</div>
                  <div className="mt-2 text-white text-base font-medium tracking-tight">{product.name}</div>
                  <div className="mt-0.5 text-[#B7BCC7] text-[11px]">{product.strength} · Lyophilised</div>
                  <div className="mt-3 h-px bg-[#2A2A2A]" />
                  <div className="mt-2 flex justify-between text-[10px] text-[#6E7784] font-mono">
                    <span>LOT 24-{product.id.toUpperCase()}</span><span>{product.purity}</span>
                  </div>
                </div>
                <div className="absolute left-0 right-0 bottom-[58%] h-[2px] bg-[#1565FF]/70 blur-[2px]" />
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="label-mono flex items-center gap-3">
            {product.is_bundle ? "Bundle · Starter Kit" : product.category.replace("-", " · ")}
            {product.is_bundle && (
              <span className="px-2 py-0.5 rounded-full bg-[#1565FF] text-white text-[10px] tracking-widest">SAVE £{((product.original_price || 0) - product.price).toFixed(2)}</span>
            )}
          </div>
          <h1 className="mt-4 text-4xl lg:text-5xl tracking-tight text-white font-medium">{product.name}</h1>
          <div className="mt-3 flex items-center gap-4 flex-wrap">
            <span className="text-white font-mono text-sm">{product.strength}</span>
            <span className="w-1 h-1 rounded-full bg-[#2A2A2A]" />
            <span className="inline-flex items-center gap-2 text-white font-mono text-sm">
              <ShieldCheck size={14} className="text-[#1565FF]" /> {product.purity}
            </span>
            {ratingSummary.count > 0 && (
              <>
                <span className="w-1 h-1 rounded-full bg-[#2A2A2A]" />
                <a href="#reviews" className="inline-flex items-center gap-2 text-sm text-[#B7BCC7] hover:text-white transition-colors" data-testid="product-rating-summary">
                  <StarRating value={ratingSummary.avg} size={13} />
                  <span className="font-mono">{ratingSummary.avg.toFixed(1)} · {ratingSummary.count} review{ratingSummary.count === 1 ? "" : "s"}</span>
                </a>
              </>
            )}
          </div>

          <p className="mt-6 text-[#B7BCC7] leading-relaxed">{product.long_description}</p>

          {product.is_bundle && product.includes && (
            <div className="mt-6 p-5 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]">
              <div className="label-mono">Included in this bundle</div>
              <ul className="mt-3 space-y-2">
                {product.includes.map((line) => (
                  <li key={line} className="flex items-center gap-3 text-white text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1565FF]" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            {product.benefits.map((b) => (
              <span key={b} className="px-3 py-1.5 rounded-full text-xs border border-[#2A2A2A] text-[#B7BCC7]">
                {b}
              </span>
            ))}
          </div>

          <div className="mt-10 flex items-end justify-between border-t border-[#2A2A2A] pt-8">
            <div>
              <div className="label-mono">Price</div>
              <div className="mt-2 flex items-baseline gap-3">
                {product.original_price && product.original_price > product.price && (
                  <span className="text-[#6E7784] font-mono text-lg line-through">£{product.original_price.toFixed(2)}</span>
                )}
                <div className="text-white text-4xl font-medium tabular-nums font-mono">
                  £{product.price.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 border border-[#2A2A2A] rounded-full px-2 h-11" data-testid="qty-selector">
              <button
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full hover:bg-[#171717] grid place-items-center"
                data-testid="qty-decrement"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="w-6 text-center text-white font-mono" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty((q) => Math.min(50, q + 1))}
                className="w-8 h-8 rounded-full hover:bg-[#171717] grid place-items-center"
                data-testid="qty-increment"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <button onClick={onAdd} className="mt-6 btn-primary w-full" data-testid="add-to-cart-btn">
            Add to Cart · £{(product.price * qty).toFixed(2)}
          </button>

          <div className="mt-6 p-4 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]">
            <div className="label-mono">Research Use Only</div>
            <p className="mt-2 text-[13px] text-[#B7BCC7] leading-relaxed">
              This product is intended solely for in-vitro laboratory research.
              It is not a drug, food, or cosmetic and is not for human or animal consumption.
            </p>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section id="reviews" className="mt-24 pt-12 border-t border-[#2A2A2A]" data-testid="product-reviews-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="label-mono">Reviews</div>
            <h2 className="mt-4 text-3xl lg:text-4xl tracking-tight text-white font-medium">
              What researchers say about {product.name}.
            </h2>
          </div>
          <div className="lg:col-span-4">
            {ratingSummary.count > 0 ? (
              <div className="p-5 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e]">
                <div className="label-mono">Product rating</div>
                <div className="mt-2 flex items-baseline gap-3">
                  <span className="text-white text-3xl font-medium font-mono tabular-nums">{ratingSummary.avg.toFixed(1)}</span>
                  <span className="text-[#6E7784] font-mono text-sm">/ 5</span>
                </div>
                <div className="mt-2"><StarRating value={ratingSummary.avg} size={14} /></div>
                <div className="mt-3 text-[11px] font-mono text-[#6E7784]">
                  {ratingSummary.count} verified review{ratingSummary.count === 1 ? "" : "s"}
                </div>
              </div>
            ) : (
              <div className="p-5 border border-[#2A2A2A] rounded-2xl bg-[#0e0e0e] text-[#6E7784] text-sm">
                No reviews yet — be the first to leave one.
              </div>
            )}
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>
        )}

        <div className="mt-12">
          <ReviewForm productSlug={product.slug} onSubmitted={onReviewSubmitted} />
        </div>
      </section>
    </div>
  );
}
